import { useState, useEffect, type ReactNode } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useApi } from '../api';
import { usePermissions } from '../auth';
import RoleTable, { type RoleSummary } from '../components/roles/RoleTable';
import RoleFormModal from '../components/roles/RoleFormModal';
import RoleDetailPanel from '../components/roles/RoleDetailPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PageTitle } from '../components/layout/TitleBar';

export interface RolesPageProps {
  /** Display names for permission categories (node-specific). */
  categoryLabels?: Record<string, string>;
  /** Optional group-access editor slot for the form (local node only). */
  renderGroupAccess?: (selectedIds: string[], onChange: (ids: string[]) => void) => ReactNode;
  /** Show role→group-access in the detail panel (local node only). */
  showGroupAccess?: boolean;
  /** Root container classes. */
  className?: string;
}

/**
 * Roles & permissions management — shared by both dashboards. Data flows through the
 * injected API (useApi().roles) and gates on injected permissions (usePermissions).
 * Node-specific bits (permission category labels, role→group access) come in via props.
 */
export default function RolesPage({ categoryLabels, renderGroupAccess, showGroupAccess, className = 'space-y-3' }: RolesPageProps) {
  const { roles: rolesApi } = useApi();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRole, setEditRole] = useState<any>(null);
  const [viewRoleId, setViewRoleId] = useState<string | null>(null);
  const [deleteRole, setDeleteRole] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRoles();
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadRoles() {
    setLoading(true);
    setError('');
    try {
      const data = await rolesApi.list();
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }

  async function loadPermissions() {
    try {
      setAllPermissions(await rolesApi.getPermissions());
    } catch {
      /* silent */
    }
  }

  async function handleCreate(data: any) {
    await rolesApi.create(data);
    setShowForm(false);
    loadRoles();
  }

  async function handleUpdate(data: any) {
    await rolesApi.update(editRole.id, data);
    setEditRole(null);
    loadRoles();
  }

  async function handleDelete() {
    try {
      await rolesApi.delete(deleteRole.id);
      setDeleteRole(null);
      loadRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete role');
      setDeleteRole(null);
    }
  }

  async function handleEditFromDetail(role: any) {
    try {
      const full = await rolesApi.get(role.id);
      setViewRoleId(null);
      setEditRole(full);
    } catch {
      setEditRole(role);
      setViewRoleId(null);
    }
  }

  async function handleEditClick(role: any) {
    try {
      setEditRole(await rolesApi.get(role.id));
    } catch {
      setEditRole(role);
    }
  }

  return (
    <div className={className}>
      {/* Only rendered under the "Users & Roles" tabs page, so it keeps that title. */}
      <PageTitle title="Users & Roles" subtitle="Roles and the permissions they grant">
        {hasPermission('roles.create') && (
          <button
            onClick={() => { setShowForm(true); setEditRole(null); }}
            className="flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-[13px] font-semibold text-white hover:bg-brand-dark transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Create Role
          </button>
        )}
      </PageTitle>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-[13px] text-red-700 dark:text-red-400 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-[11px] font-medium">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <RoleTable
          roles={roles}
          isSuperAdmin={isSuperAdmin}
          onView={(role) => setViewRoleId(role.id)}
          onEdit={hasPermission('roles.edit') ? handleEditClick : undefined}
          onDelete={hasPermission('roles.delete') ? (role) => setDeleteRole(role) : undefined}
        />
      )}

      {(showForm || editRole) && (
        <RoleFormModal
          role={editRole}
          allPermissions={allPermissions}
          onSubmit={editRole ? handleUpdate : handleCreate}
          onCancel={() => { setShowForm(false); setEditRole(null); }}
          renderGroupAccess={renderGroupAccess}
          readOnly={!!editRole && editRole.name === 'super_admin' && !isSuperAdmin}
        />
      )}

      {viewRoleId && (
        <RoleDetailPanel
          roleId={viewRoleId}
          categoryLabels={categoryLabels}
          showGroupAccess={showGroupAccess}
          onClose={() => setViewRoleId(null)}
          onEdit={hasPermission('roles.edit') ? handleEditFromDetail : undefined}
        />
      )}

      <ConfirmDialog
        open={!!deleteRole}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${deleteRole?.name?.replace(/_/g, ' ')}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteRole(null)}
      />
    </div>
  );
}
